import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MailIcon, LockIcon, EyeIcon, EyeOffIcon, ZapIcon, ShieldCheckIcon, BarChart3Icon, ArrowRightIcon } from 'lucide-react';
import type { Usuario } from '../types';
import { T, useIsMobile } from '../ui';
import { LOGIN_FUNCTION_URL, SUPABASE_ANON_KEY } from '../supabase';
const LOGO = "/pasted-image.png";
const STATS: {
  value: string;
  label: string;
}[] = [{
  value: '3',
  label: 'Sucursales'
}, {
  value: '512k',
  label: 'kWh / mes'
}, {
  value: '24/7',
  label: 'Monitoreo'
}];
const FEATURES: {
  icon: React.ReactNode;
  title: string;
  desc: string;
}[] = [{
  icon: <ZapIcon size={18} data-id="element-570" />,
  title: 'Consumo en tiempo real',
  desc: 'Registra la lectura del medidor y deja que el sistema calcule el resto.'
}, {
  icon: <BarChart3Icon size={18} data-id="element-571" />,
  title: 'Reportes claros',
  desc: 'Tendencias y costos por sucursal, listos para exportar.'
}, {
  icon: <ShieldCheckIcon size={18} data-id="element-572" />,
  title: 'Accesos por rol',
  desc: 'Cada usuario ve únicamente las sucursales que le corresponden.'
}];
export function Login({
  onLogin
}: {
  onLogin: (usuario: Usuario) => void;
}) {
  const isMobile = useIsMobile(880);
  const [correo, setCorreo] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);
  const tryLogin = async (mail: string, pass: string) => {
    if (cargando) return;
    setCargando(true);
    setError('');
    try {
      const res = await fetch(LOGIN_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          correo: mail.trim(),
          password: pass
        })
      });
      const data = await res.json();
      if (!res.ok || !data?.usuario) {
        setError(data?.error ?? 'Correo o contraseña incorrectos.');
        return;
      }
      const u = data.usuario;
      onLogin({
        id: u.id,
        nombre: u.nombre,
        correo: u.correo,
        rol: u.rol,
        sucursalesIds: Array.isArray(u.sucursales_ids) ? u.sucursales_ids : [],
        archivado: !!u.archivado
      });
    } catch {
      setError('No se pudo conectar con el servidor. Intenta de nuevo.');
    } finally {
      setCargando(false);
    }
  };
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    void tryLogin(correo, password);
  };
  const fieldWrap: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  };
  const fieldInput: React.CSSProperties = {
    width: '100%',
    padding: '14px 14px 14px 46px',
    background: 'rgba(0,0,0,0.03)',
    border: `1px solid ${T.border}`,
    borderRadius: 14,
    fontSize: 14,
    color: '#1A1207',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box'
  };
  const leadingIcon: React.CSSProperties = {
    position: 'absolute',
    left: 16,
    color: 'rgba(26,18,7,.4)',
    display: 'flex',
    pointerEvents: 'none'
  };
  return <div style={{
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    position: 'relative',
    overflow: 'hidden',
    background: '#FFFCF8'
  }} data-id="element-573">
      
      <style data-id="element-574">{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap');
        .login2 * { font-family: 'DM Sans', sans-serif; }
        .login2 h1, .login2 .title { font-family: 'Space Grotesk', sans-serif; }
        .login2-input:focus {
          border-color: rgba(255,107,1,.6) !important;
          box-shadow: 0 0 0 3px rgba(255,107,1,.14) !important;
          background: rgba(0,0,0,.05) !important;
        }
        .login2-demo { transition: background .15s, border-color .15s, transform .12s; }
        .login2-demo:hover { background: rgba(0,0,0,.06) !important; border-color: rgba(255,107,1,.4) !important; transform: translateY(-1px); }
        .login2-eye { transition: color .15s; }
        .login2-eye:hover { color: #1A1207 !important; }
        /* animated brand gradient submit with shimmer sweep */
        .login2-submit {
          position: relative;
          overflow: hidden;
          transition: transform .16s, box-shadow .16s, filter .16s;
        }
        .login2-submit:hover { transform: translateY(-1px); filter: brightness(1.06); box-shadow: 0 14px 38px rgba(255,107,1,.3), inset 0 1px 0 rgba(255,255,255,.32); }
        .login2-submit::after {
          content: '';
          position: absolute; top: 0; left: -130%;
          width: 60%; height: 100%;
          background: linear-gradient(100deg, transparent, rgba(255,255,255,.45), transparent);
          transform: skewX(-18deg);
          transition: left .55s ease;
        }
        .login2-submit:hover::after { left: 130%; }
        /* aurora drift blobs */
        @keyframes auroraA { 0%,100%{ transform: translate(0,0) scale(1) } 33%{ transform: translate(40px,30px) scale(1.12) } 66%{ transform: translate(-30px,20px) scale(.95) } }
        @keyframes auroraB { 0%,100%{ transform: translate(0,0) scale(1) } 50%{ transform: translate(-50px,-30px) scale(1.15) } }
        @keyframes auroraC { 0%,100%{ transform: translate(0,0) scale(1) } 50%{ transform: translate(30px,-40px) scale(1.1) } }
        /* rotating conic sheen on the card border */
        @keyframes spin360 { to { transform: rotate(360deg) } }
        @keyframes pulseDot { 0%,100%{ opacity:1; transform: scale(1) } 50%{ opacity:.4; transform: scale(.7) } }
        .login2-cardborder::before {
          content: '';
          position: absolute; inset: -1px;
          border-radius: 25px;
          padding: 1px;
          background: conic-gradient(from 0deg, transparent 0deg, rgba(255,107,1,.55) 60deg, rgba(212,225,0,.5) 140deg, transparent 220deg, transparent 360deg);
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          animation: spin360 9s linear infinite;
          pointer-events: none;
          opacity: .7;
        }
      `}</style>

      {/* ── ANIMATED AURORA BACKGROUND ── */}
      <div aria-hidden style={{
      position: 'absolute',
      inset: 0,
      overflow: 'hidden',
      pointerEvents: 'none'
    }} data-id="element-575">
        
        <div style={{
        position: 'absolute',
        top: '-18%',
        left: '-10%',
        width: 520,
        height: 520,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,107,1,.12), transparent 68%)',
        filter: 'blur(40px)',
        animation: 'auroraA 22s ease-in-out infinite'
      }} data-id="element-576" />
        
        <div style={{
        position: 'absolute',
        top: '30%',
        left: '40%',
        width: 460,
        height: 460,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(212,225,0,.15), transparent 68%)',
        filter: 'blur(46px)',
        animation: 'auroraB 26s ease-in-out infinite'
      }} data-id="element-577" />
        
        <div style={{
        position: 'absolute',
        bottom: '-20%',
        right: '-8%',
        width: 560,
        height: 560,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,0,0,.08), transparent 66%)',
        filter: 'blur(48px)',
        animation: 'auroraC 30s ease-in-out infinite'
      }} data-id="element-578" />
        
        {/* subtle tech dot-grid */}
        <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: 'radial-gradient(rgba(0,0,0,.05) 1px, transparent 1px)',
        backgroundSize: '26px 26px',
        maskImage: 'radial-gradient(ellipse at center, #000 30%, transparent 78%)',
        WebkitMaskImage: 'radial-gradient(ellipse at center, #000 30%, transparent 78%)'
      }} data-id="element-579" />
        
      </div>

      <div className="login2" style={{
      position: 'relative',
      zIndex: 1,
      width: '100%',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: 'stretch'
    }} data-id="element-580">
        
        {/* ── BRAND PANEL ── */}
        <motion.div initial={{
        opacity: 0,
        x: isMobile ? 0 : -24,
        y: isMobile ? -16 : 0
      }} animate={{
        opacity: 1,
        x: 0,
        y: 0
      }} transition={{
        duration: 0.5,
        ease: [0.2, 0.8, 0.2, 1]
      }} style={{
        flex: isMobile ? 'none' : '1 1 52%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: isMobile ? 'center' : 'flex-start',
        gap: isMobile ? 16 : 28,
        padding: isMobile ? '56px 24px 12px' : '56px 64px'
      }} data-id="element-581">
          
          <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: isMobile ? 'center' : 'flex-start',
          gap: isMobile ? 16 : 14,
          flexWrap: 'wrap'
        }} data-id="element-582">
            
            <img src="https://cdn.magicpatterns.com/uploads/6VHLBX9XSQjy9HjvYqPJBL/pasted-image.png" alt="Junquito Gas" style={{
            width: isMobile ? 220 : 260,
            maxWidth: isMobile ? '78%' : '64%',
            height: 'auto',
            objectFit: 'contain',
            display: 'block',
            margin: isMobile ? '0 auto' : undefined
          }} data-id="element-583" />
            
          </div>

          {!isMobile && <>
              <div data-id="element-584">
                <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: T.accent,
              marginBottom: 14
            }} data-id="element-585">
                
                  <span style={{
                width: 16,
                height: 2,
                borderRadius: 2,
                background: T.accent,
                boxShadow: `0 0 10px ${T.accent}`
              }} data-id="element-586" />
                
                  Plataforma Junquito Gas
                </div>
                <h1 className="title" style={{
              fontSize: 42,
              lineHeight: 1.04,
              fontWeight: 800,
              letterSpacing: '-0.03em',
              color: '#1A1207',
              margin: 0,
              maxWidth: 470
            }} data-id="element-587">
                
                  Control de consumo energético comercial
                </h1>
                <p style={{
              marginTop: 14,
              fontSize: 15,
              lineHeight: 1.6,
              color: T.textSecondary,
              maxWidth: 430
            }} data-id="element-588">
                
                  Centraliza la lectura de medidores de todas tus sucursales y
                  toma decisiones con datos claros.
                </p>
              </div>

              {/* headline stats strip */}
              <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} style={{
            display: 'flex',
            maxWidth: 420,
            background: 'rgba(0,0,0,0.02)',
            border: `1px solid ${T.border}`,
            borderRadius: 18,
            overflow: 'hidden',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,.6)'
          }} data-id="element-589">
              
                {STATS.map((s, i) => <div key={s.label} style={{
              flex: 1,
              padding: '18px 16px',
              textAlign: 'center',
              borderLeft: i === 0 ? 'none' : `1px solid ${T.border}`
            }} data-id="element-590">
                
                    <div style={{
                fontSize: 24,
                fontWeight: 800,
                color: T.accent,
                fontFamily: "'Space Grotesk', sans-serif",
                letterSpacing: '-0.02em'
              }} data-id="element-591">
                  
                      {s.value}
                    </div>
                    <div style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: T.textMuted,
                marginTop: 4
              }} data-id="element-592">
                  
                      {s.label}
                    </div>
                  </div>)}
              </motion.div>

              <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            maxWidth: 440
          }} data-id="element-593">
              
                {FEATURES.map((f, i) => <motion.div key={f.title} initial={{
              opacity: 0,
              x: -14
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.4,
              delay: 0.35 + i * 0.08,
              ease: [0.2, 0.8, 0.2, 1]
            }} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 13
            }} data-id="element-594">
                
                    <div style={{
                width: 38,
                height: 38,
                flexShrink: 0,
                borderRadius: 11,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: T.accentSoft,
                border: `1px solid ${T.accentBorder}`,
                color: T.accent
              }} data-id="element-595">
                  
                      {f.icon}
                    </div>
                    <div data-id="element-596">
                      <div style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: '#1A1207'
                }} data-id="element-597">
                    
                        {f.title}
                      </div>
                      <div style={{
                  fontSize: 13,
                  color: T.textSecondary,
                  marginTop: 2,
                  lineHeight: 1.5
                }} data-id="element-598">
                    
                        {f.desc}
                      </div>
                    </div>
                  </motion.div>)}
              </div>
            </>}
        </motion.div>

        {/* ── AUTH PANEL ── */}
        <div style={{
        flex: isMobile ? '1 1 auto' : '1 1 48%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '8px 20px 40px' : '40px 56px'
      }} data-id="element-599">
          
          <motion.div initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: [0.2, 0.8, 0.2, 1]
        }} style={{
          width: '100%',
          maxWidth: 424
        }} data-id="element-600">
            
            <div className="login2-cardborder" style={{
            position: 'relative',
            background: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(30px) saturate(180%)',
            WebkitBackdropFilter: 'blur(30px) saturate(180%)',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 24,
            padding: isMobile ? 24 : 36,
            boxShadow: '0 50px 110px rgba(0,0,0,.08), inset 0 1px 0 rgba(255,255,255,.6)'
          }} data-id="element-601">
              
              <div style={{
              marginBottom: 24
            }} data-id="element-602">
                
                <h2 className="title" style={{
                fontSize: isMobile ? 22 : 25,
                lineHeight: 1.1,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                color: '#1A1207',
                margin: 0
              }} data-id="element-603">
                  
                  Bienvenido de nuevo
                </h2>
                <div style={{
                fontSize: 13.5,
                color: T.textSecondary,
                marginTop: 6
              }} data-id="element-604">
                  
                  Ingresa tus credenciales para continuar
                </div>
              </div>

              <form onSubmit={submit} data-id="element-605">
                <div style={{
                marginBottom: 14
              }} data-id="element-606">
                  
                  <label style={{
                  display: 'block',
                  fontSize: 11,
                  fontWeight: 700,
                  color: T.textMuted,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  marginBottom: 8
                }} data-id="element-607">
                    
                    Correo
                  </label>
                  <div style={fieldWrap} data-id="element-608">
                    <span style={leadingIcon} data-id="element-609">
                      <MailIcon size={17} data-id="element-610" />
                    </span>
                    <input className="login2-input" type="text" value={correo} onChange={e => setCorreo(e.target.value)} placeholder="correo@junquitogas.com" style={fieldInput} autoComplete="username" data-id="element-611" />
                    
                  </div>
                </div>

                <div style={{
                marginBottom: 18
              }} data-id="element-612">
                  
                  <label style={{
                  display: 'block',
                  fontSize: 11,
                  fontWeight: 700,
                  color: T.textMuted,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  marginBottom: 8
                }} data-id="element-613">
                    
                    Contraseña
                  </label>
                  <div style={fieldWrap} data-id="element-614">
                    <span style={leadingIcon} data-id="element-615">
                      <LockIcon size={17} data-id="element-616" />
                    </span>
                    <input className="login2-input" type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" style={{
                    ...fieldInput,
                    paddingRight: 46
                  }} autoComplete="current-password" data-id="element-617" />
                    
                    <button type="button" onClick={() => setShowPass(s => !s)} aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'} className="login2-eye" style={{
                    position: 'absolute',
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(26,18,7,.4)',
                    padding: 6,
                    display: 'flex',
                    cursor: 'pointer'
                  }} data-id="element-618">
                      
                      {showPass ? <EyeOffIcon size={17} data-id="element-619" /> : <EyeIcon size={17} data-id="element-620" />}
                    </button>
                  </div>
                </div>

                {error && <motion.div initial={{
                opacity: 0,
                y: -6
              }} animate={{
                opacity: 1,
                y: 0
              }} style={{
                background: T.dangerSoft,
                border: `1px solid rgba(220,38,38,.3)`,
                color: '#dc2626',
                borderRadius: 11,
                padding: '10px 14px',
                fontSize: 13,
                marginBottom: 16
              }} data-id="element-621">
                  
                    {error}
                  </motion.div>}

                <button type="submit" disabled={cargando} className="login2-submit" style={{
                width: '100%',
                padding: '14px 20px',
                border: '1px solid rgba(255,107,1,.4)',
                borderRadius: 14,
                background: 'linear-gradient(135deg, #FF8C00 0%, #FF5A00 100%)',
                color: '#ffffff',
                fontWeight: 800,
                fontSize: 14.5,
                letterSpacing: '0.01em',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                cursor: cargando ? 'default' : 'pointer',
                opacity: cargando ? 0.7 : 1,
                boxShadow: '0 6px 24px rgba(255,107,1,0.3), inset 0 1px 0 rgba(255,255,255,.25)'
              }} data-id="element-622">
                  
                  {cargando ? 'Entrando…' : 'Entrar'}
                  {!cargando && <ArrowRightIcon size={17} data-id="element-623" />}
                </button>
              </form>
            </div>

            <div style={{
            marginTop: 18,
            fontSize: 12,
            color: T.textMuted,
            textAlign: 'center'
          }} data-id="element-624">
              
              Junquito Gas
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}